import { AutoAwesomeOutlined, FastForwardOutlined } from "@mui/icons-material";
import { Box, BoxProps, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import React from "react";

import { ToggleButton } from "@/Components/v2/atoms/Button";
import { ToggleButtonGroup } from "@/Components/v2/molecule/ButtonGroup";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";

import { useChat } from "../../context/ChatContext";
import { ModelSelect } from "./ModelSelect";
import { PromptBuilder } from "./PromptBuilder";
import { QuickQuery } from "./QueryPlaceholders";

export const Welcome: React.FC<BoxProps> = (props) => <Box {...props}></Box>;

// 2. Empty chat prompt
interface EmptyChatProps {
  onAskQuestion: (question: string, warcEnabled: boolean) => void;
  warcEnabledForTier: boolean;
}

const sampleQuestions = [
  "What are my best and worst performing channels?",
  "Suggest a safe test-and-learn budget split across channels based on current performance.",
  "Which underperforming channels could we reduce to free up budget for testing?",
];

export const EmptyChat: React.FC<EmptyChatProps> = ({ onAskQuestion, warcEnabledForTier }) => {
  const { value: isQueryToggleFeatureEnabled } = useFeatureGate(featureFlag.maiteQueryToggle);

  const [selectedToggle, setSelectedToggle] = React.useState<"quick" | "builder">("builder");

  const { metadata, isAsking, chatMessages } = useChat();

  const queryLimitReached = metadata?.queryLimitReached || false;
  const remainingQueries = metadata?.remainingQueries || 0;
  const isFreeUser = metadata?.hendrenTier === "FREE";

  // Get the latest message with preSetConstraints to pre-populate the form
  const latestPreSetConstraints = React.useMemo(() => {
    if (!chatMessages || chatMessages.length === 0) return undefined;

    // Find the most recent maite message with preSetConstraints
    for (let i = chatMessages.length - 1; i >= 0; i--) {
      const message = chatMessages[i];
      if (
        message &&
        message.user === "maite" &&
        "preSetConstraints" in message &&
        message.preSetConstraints
      ) {
        return message.preSetConstraints;
      }
    }
    return undefined;
  }, [chatMessages]);

  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: "quick" | "builder" | null
  ) => {
    if (newValue !== null) {
      setSelectedToggle(newValue);
    }
  };

  return (
    <Stack direction="column" gap={3}>
      <ModelSelect />
      <Typography variant="h1" mb={3} sx={{ textAlign: "center" }}>
        MAITE. Powered by Proof
      </Typography>
      {isQueryToggleFeatureEnabled && (
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mb={2}>
          <ToggleButtonGroup
            exclusive
            value={selectedToggle}
            onChange={handleToggleChange}
            aria-label="quick query or prompt builder"
            size="small"
            sx={{
              background: (theme) => theme.palette.background.paper,
              border: "1px solid #E7E7E7",
              borderRadius: "18px",
              "& .MuiToggleButton-root": {
                borderRadius: "20px",
                border: "none",
                fontWeight: 400,
                "&.Mui-selected": {
                  background: "linear-gradient(90deg, #0E2244 0%, #EC0089 100%)",
                  color: "white",
                  fontWeight: 600,
                  "&:hover": {
                    background: "linear-gradient(90deg, #0E2244 0%, #EC0089 100%)",
                  },
                },
              },
            }}
          >
            <ToggleButton value="quick" aria-label="quick query">
              <FastForwardOutlined sx={{ mr: 1, fontSize: "20px" }} />
              Quick Query
            </ToggleButton>
            <ToggleButton value="builder" aria-label="prompt builder">
              <AutoAwesomeOutlined sx={{ mr: 1, fontSize: "20px" }} />
              Prompt Builder
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}
      <Stack
        sx={{
          maxWidth: "900px",
          width: "100%",
          mx: "auto",
          border: "1px solid #E7E7E7",
          borderRadius: "9px",
        }}
        p={3}
      >
        {selectedToggle === "quick" ? (
          <QuickQuery
            queryLimitReached={queryLimitReached}
            isAsking={isAsking}
            askQuestion={onAskQuestion}
            warcEnabledForTier={warcEnabledForTier}
            sampleQuestions={sampleQuestions}
            isFreeUser={isFreeUser}
            remainingQueries={remainingQueries}
          />
        ) : (
          <PromptBuilder preSetConstraints={latestPreSetConstraints} />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          MAITE can make mistakes. Consider checking important information.
        </Typography>
      </Stack>
    </Stack>
  );
};

// 3. Loading skeleton view
export const LoadingChat: React.FC = () => {
  const theme = useTheme();
  const { chatTheme } = theme;
  return (
    <Box flex={1} display="flex" flexDirection="column" alignSelf="stretch" p={3}>
      {[3, 4, 6].map((h) => (
        <Skeleton
          key={h}
          variant="rounded"
          height={`${h}rem`}
          width="100%"
          sx={{ borderRadius: chatTheme.borderRadius.skeleton, mb: chatTheme.spacing.statusMargin }}
        />
      ))}
    </Box>
  );
};
